import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { FaPlus, FaEdit, FaCopy, FaSearch, FaDumbbell, FaCog, FaChartBar, FaToggleOn, FaToggleOff, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============= CONSTANTS =============
// 2026-02 · Vocabulario unificado con el backend (canonicalize_muscle en
// training_manager.py). gluteo→gluteos, isquios→femoral, lumbar→lumbares.
const MUSCLE_GROUPS = [
  { value: 'gluteos', label: 'Glúteos' },
  { value: 'cuadriceps', label: 'Cuádriceps' },
  { value: 'femoral', label: 'Femoral' },
  { value: 'aductores', label: 'Aductores' },
  { value: 'abductores', label: 'Abductores' },
  { value: 'pantorrilla', label: 'Pantorrilla' },
  { value: 'espalda', label: 'Espalda' },
  { value: 'pecho', label: 'Pecho' },
  { value: 'hombros', label: 'Hombros' },
  { value: 'biceps', label: 'Bíceps' },
  { value: 'triceps', label: 'Tríceps' },
  { value: 'core', label: 'Core' },
  { value: 'lumbares', label: 'Lumbares' },
  { value: 'antebrazos', label: 'Antebrazos' }
];

const CATEGORIES = [
  { value: 'gluteos', label: 'Gluteos' },
  { value: 'piernas', label: 'Piernas' },
  { value: 'espalda', label: 'Espalda' },
  { value: 'pecho', label: 'Pecho' },
  { value: 'hombro', label: 'Hombro' },
  { value: 'biceps', label: 'Biceps' },
  { value: 'triceps', label: 'Triceps' },
  { value: 'core', label: 'Core' },
  { value: 'brazos', label: 'Brazos' },
  { value: 'cuerpo_completo', label: 'Cuerpo Completo' }
];

const EQUIPMENT_TYPES = [
  { value: 'barra', label: 'Barra' },
  { value: 'mancuernas', label: 'Mancuernas' },
  { value: 'maquina', label: 'Maquina' },
  { value: 'polea', label: 'Polea' },
  { value: 'peso_corporal', label: 'Peso Corporal' },
  { value: 'banda', label: 'Banda' },
  { value: 'smith', label: 'Smith' },
  { value: 'landmine', label: 'Landmine' },
  { value: 'disco', label: 'Disco' },
  { value: 'kettlebell', label: 'Kettlebell' },
  { value: 'otro', label: 'Otro' }
];

const LEVELS = [
  { value: 'basico', label: 'Basico' },
  { value: 'intermedio', label: 'Intermedio' },
  { value: 'avanzado', label: 'Avanzado' }
];

const MOVEMENT_PATTERNS = [
  { value: 'empuje', label: 'Empuje' },
  { value: 'traccion', label: 'Traccion' },
  { value: 'bisagra_de_cadera', label: 'Bisagra de cadera' },
  { value: 'sentadilla', label: 'Sentadilla' },
  { value: 'zancada', label: 'Zancada' },
  { value: 'abduccion', label: 'Abduccion' },
  { value: 'aduccion', label: 'Aduccion' },
  { value: 'extension_cadera', label: 'Extension de cadera' },
  { value: 'flexion', label: 'Flexion' },
  { value: 'rotacion', label: 'Rotacion' },
  { value: 'isometrico', label: 'Isometrico' },
  { value: 'otro', label: 'Otro' }
];

const METHOD_TYPES = [
  { value: 'Volumen', label: 'Volumen' },
  { value: 'Densidad', label: 'Densidad' },
  { value: 'Control', label: 'Control' },
  { value: 'Intensidad', label: 'Intensidad' },
  { value: 'Normal', label: 'Normal' },
  { value: 'Otro', label: 'Otro' }
];

const EMPTY_EXERCISE = {
  name: '', slug: '', status: 'active', primary_muscle: '', secondary_muscle: '',
  category: '', movement_pattern: '', laterality: 'bilateral', compound_type: 'compuesto',
  level: 'intermedio', equipment: '', location: 'ambos', allows_series: true, allows_reps: true,
  allows_time: false, allows_distance: false, allows_load: true, allows_rir: true,
  allows_tempo: true, allows_rest: true, allows_notes: true, description: '',
  cues: '', common_errors: '', video_url: '', image_url: '', contraindications: '',
  alternatives: '', tags: [], sort_order: 0
};

const EMPTY_METHOD = {
  name: '', slug: '', status: 'active', method_type: '', description: '',
  instructions: '', base_structure: '', allows_rounds: false,
  allows_grouped_exercises: false, allows_rest_between_exercises: true,
  allows_rest_between_rounds: false, allows_reps: true, allows_time: false,
  allows_load: true, allows_notes: true, display_format: '', storage_format: '',
  visual_block_format: '', priority: 0, compatibility: 'both', example: '', icon: ''
};

// ============= STATS COMPONENT =============
const TrainingStats = ({ stats }) => {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <Card className="bg-gradient-to-br from-emerald-600 to-green-700 border-0">
        <CardContent className="pt-4 pb-4">
          <p className="text-emerald-200 text-xs font-medium">Ejercicios Activos</p>
          <p className="text-2xl font-bold text-white" data-testid="stat-active-exercises">{stats.active_exercises}</p>
        </CardContent>
      </Card>
      <Card className="bg-gradient-to-br from-gray-600 to-gray-700 border-0">
        <CardContent className="pt-4 pb-4">
          <p className="text-gray-300 text-xs font-medium">Ejercicios Inactivos</p>
          <p className="text-2xl font-bold text-white" data-testid="stat-inactive-exercises">{stats.inactive_exercises}</p>
        </CardContent>
      </Card>
      <Card className="bg-gradient-to-br from-blue-600 to-indigo-700 border-0">
        <CardContent className="pt-4 pb-4">
          <p className="text-blue-200 text-xs font-medium">Metodos Activos</p>
          <p className="text-2xl font-bold text-white" data-testid="stat-active-methods">{stats.active_methods}</p>
        </CardContent>
      </Card>
      <Card className="bg-gradient-to-br from-gray-600 to-gray-700 border-0">
        <CardContent className="pt-4 pb-4">
          <p className="text-gray-300 text-xs font-medium">Metodos Inactivos</p>
          <p className="text-2xl font-bold text-white" data-testid="stat-inactive-methods">{stats.inactive_methods}</p>
        </CardContent>
      </Card>
    </div>
  );
};

// ============= EXERCISE FORM =============
// ─────────────────────────────────────────────────────────
// VideoLinker: buscador para linkear un ejercicio con un video
// del gestor de contenido. Muestra los matches por nombre (default
// usa el nombre del ejercicio) y populates video_url al seleccionar.
// ─────────────────────────────────────────────────────────
const VideoLinker = ({ value, exerciseName, onSelect }) => {
  const [query, setQuery] = useState(exerciseName || '');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState('');

  React.useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (query.trim()) params.append('q', query.trim());
        params.append('limit', '15');
        const res = await fetch(`${API_URL}/api/admin/training-manager/videos-search?${params}`);
        if (!res.ok) throw new Error('search failed');
        const data = await res.json();
        setResults(data.videos || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open]);

  return (
    <div className="relative">
      <div className="flex gap-1">
        <Input
          data-testid="exercise-video-linker-input"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={(e) => { setOpen(true); e.target.select(); }}
          placeholder="Buscar video por nombre… (o borra para ver todos)"
          className="bg-gray-800 border-gray-600 text-white flex-1"
        />
        {value && (
          <button
            type="button"
            onClick={() => { onSelect(null); setSelectedTitle(''); }}
            title="Quitar video vinculado"
            className="px-2 py-1 rounded bg-red-600/20 text-red-300 text-xs hover:bg-red-600/30"
            data-testid="exercise-video-linker-clear"
          >✕</button>
        )}
      </div>
      {value && (
        <p className="text-[10px] text-emerald-300 mt-1 truncate">
          ✓ Vinculado: {selectedTitle || value}
        </p>
      )}
      {open && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-700 bg-gray-900 shadow-xl"
          data-testid="exercise-video-linker-results"
        >
          {loading ? (
            <p className="p-2 text-xs text-gray-400">Buscando…</p>
          ) : results.length === 0 ? (
            <p className="p-2 text-xs text-gray-500">
              No hay videos con ese nombre en la biblioteca.
            </p>
          ) : (
            <ul>
              {results.map((v) => (
                <li
                  key={v.id}
                  onClick={() => {
                    onSelect(v);
                    setSelectedTitle(v.title);
                    setQuery(v.title);
                    setOpen(false);
                  }}
                  className="cursor-pointer px-3 py-2 text-xs hover:bg-cyan-500/10 border-b border-gray-800 last:border-b-0"
                  data-testid={`exercise-video-linker-result-${v.id}`}
                >
                  <div className="text-white font-semibold truncate">{v.title}</div>
                  <div className="text-[10px] text-gray-400 flex gap-2 mt-0.5">
                    {v.muscle_group && <span>💪 {v.muscle_group}</span>}
                    {v.recommended_sex && v.recommended_sex !== 'ambos' && (
                      <span className={v.recommended_sex === 'mujer' ? 'text-pink-300' : 'text-blue-300'}>
                        {v.recommended_sex === 'mujer' ? '👩 mujer' : '👨 hombre'}
                      </span>
                    )}
                    {v.location && <span>· {v.location}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};


const ExerciseForm = ({ exercise, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(exercise || EMPTY_EXERCISE);
  const [tagsInput, setTagsInput] = useState((exercise?.tags || []).join(', '));

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return; }
    const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
    onSave({ ...form, tags });
  };

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* Identification */}
      <div>
        <h3 className="text-sm font-semibold text-orange-400 mb-3 uppercase tracking-wide">Identificacion</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-gray-300 text-xs">Nombre *</Label>
            <Input data-testid="exercise-name-input" value={form.name} onChange={e => handleChange('name', e.target.value)} className="bg-gray-800 border-gray-600 text-white" placeholder="Ej: Hip thrust con barra" />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Slug (auto)</Label>
            <Input value={form.slug} onChange={e => handleChange('slug', e.target.value)} className="bg-gray-800 border-gray-600 text-white" placeholder="Se genera automaticamente" />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Estado</Label>
            <Select value={form.status} onValueChange={v => handleChange('status', v)}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Activo</SelectItem><SelectItem value="inactive">Inactivo</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Nivel</Label>
            <Select value={form.level} onValueChange={v => handleChange('level', v)}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>{LEVELS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Classification */}
      <div>
        <h3 className="text-sm font-semibold text-orange-400 mb-3 uppercase tracking-wide">Clasificacion</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-gray-300 text-xs">Grupo muscular principal</Label>
            <Select value={form.primary_muscle} onValueChange={v => handleChange('primary_muscle', v)}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>{MUSCLE_GROUPS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Grupo muscular secundario</Label>
            <Select value={form.secondary_muscle || "none"} onValueChange={v => handleChange('secondary_muscle', v === "none" ? "" : v)}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white"><SelectValue placeholder="Ninguno" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Ninguno</SelectItem>{MUSCLE_GROUPS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Categoria</Label>
            <Select value={form.category} onValueChange={v => handleChange('category', v)}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Patron de movimiento</Label>
            <Select value={form.movement_pattern || "otro"} onValueChange={v => handleChange('movement_pattern', v)}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>{MOVEMENT_PATTERNS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Lateralidad</Label>
            <Select value={form.laterality} onValueChange={v => handleChange('laterality', v)}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="bilateral">Bilateral</SelectItem><SelectItem value="unilateral">Unilateral</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Tipo</Label>
            <Select value={form.compound_type} onValueChange={v => handleChange('compound_type', v)}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="compuesto">Compuesto</SelectItem><SelectItem value="analitico">Analitico</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Equipment & Location */}
      <div>
        <h3 className="text-sm font-semibold text-orange-400 mb-3 uppercase tracking-wide">Implemento y lugar</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-gray-300 text-xs mb-1 block">Implemento</Label>
            <Select value={form.equipment || "otro"} onValueChange={v => handleChange('equipment', v)}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>{EQUIPMENT_TYPES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-300 text-xs mb-1 block">Lugar de entreno</Label>
            <Select value={form.location || "ambos"} onValueChange={v => handleChange('location', v)}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white" data-testid="ex-location-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="casa">🏠 Casa</SelectItem>
                <SelectItem value="gym">🏋️ Gym</SelectItem>
                <SelectItem value="ambos">🔀 Ambos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Generator Config */}
      <div>
        <h3 className="text-sm font-semibold text-orange-400 mb-3 uppercase tracking-wide">Configuracion para generador</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            ['allows_series', 'Series'], ['allows_reps', 'Repeticiones'], ['allows_time', 'Tiempo'],
            ['allows_distance', 'Distancia'], ['allows_load', 'Carga'], ['allows_rir', 'RIR'],
            ['allows_tempo', 'Tempo'], ['allows_rest', 'Descanso'], ['allows_notes', 'Observaciones']
          ].map(([key, label]) => (
            <div key={key} className="flex items-center space-x-2">
              <Checkbox id={key} checked={form[key]} onCheckedChange={v => handleChange(key, v)} />
              <Label htmlFor={key} className="text-gray-300 text-xs cursor-pointer">{label}</Label>
            </div>
          ))}
        </div>
      </div>

      {/* Execution */}
      <div>
        <h3 className="text-sm font-semibold text-orange-400 mb-3 uppercase tracking-wide">Ejecucion</h3>
        <div className="space-y-3">
          <div>
            <Label className="text-gray-300 text-xs">Descripcion tecnica</Label>
            <Textarea value={form.description} onChange={e => handleChange('description', e.target.value)} className="bg-gray-800 border-gray-600 text-white" rows={2} />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Cues / Indicaciones clave</Label>
            <Textarea value={form.cues} onChange={e => handleChange('cues', e.target.value)} className="bg-gray-800 border-gray-600 text-white" rows={2} />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Errores comunes</Label>
            <Textarea value={form.common_errors} onChange={e => handleChange('common_errors', e.target.value)} className="bg-gray-800 border-gray-600 text-white" rows={2} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-300 text-xs">Video (buscar en biblioteca)</Label>
              <VideoLinker
                value={form.video_url}
                exerciseName={form.name}
                onSelect={(v) => {
                  handleChange('video_url', v?.video_url || '');
                  if (v?.thumbnail_url) handleChange('image_url', v.thumbnail_url);
                  // Solo copiar el sexo del video si el ejercicio no tiene uno explícito ya
                  if (v?.recommended_sex && (!form.recommended_sex || form.recommended_sex === 'ambos')) {
                    handleChange('recommended_sex', v.recommended_sex);
                  }
                }}
              />
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Imagen URL</Label>
              <Input value={form.image_url} onChange={e => handleChange('image_url', e.target.value)} className="bg-gray-800 border-gray-600 text-white" placeholder="https://..." />
            </div>
          </div>
        </div>
      </div>

      {/* Advanced */}
      <div>
        <h3 className="text-sm font-semibold text-orange-400 mb-3 uppercase tracking-wide">Avanzado</h3>
        <div className="space-y-3">
          <div>
            <Label className="text-gray-300 text-xs">Contraindicaciones</Label>
            <Textarea value={form.contraindications} onChange={e => handleChange('contraindications', e.target.value)} className="bg-gray-800 border-gray-600 text-white" rows={2} />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Alternativas / Reemplazos</Label>
            <Textarea value={form.alternatives} onChange={e => handleChange('alternatives', e.target.value)} className="bg-gray-800 border-gray-600 text-white" rows={2} />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Tags (separados por coma)</Label>
            <Input value={tagsInput} onChange={e => setTagsInput(e.target.value)} className="bg-gray-800 border-gray-600 text-white" placeholder="gluteos, barra, compuesto" />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Orden de aparicion</Label>
            <Input type="number" value={form.sort_order} onChange={e => handleChange('sort_order', parseInt(e.target.value) || 0)} className="bg-gray-800 border-gray-600 text-white w-32" />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-gray-700 sticky bottom-0 bg-gray-900 pb-2">
        <Button data-testid="exercise-save-btn" onClick={handleSubmit} disabled={saving} className="bg-gradient-to-r from-orange-500 to-red-600 text-white">
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
        <Button data-testid="exercise-cancel-btn" onClick={onCancel} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800">
          Cancelar
        </Button>
      </div>
    </div>
  );
};

// ============= METHOD FORM =============
const MethodForm = ({ method, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(method || EMPTY_METHOD);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return; }
    onSave(form);
  };

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* Identification */}
      <div>
        <h3 className="text-sm font-semibold text-blue-400 mb-3 uppercase tracking-wide">Identificacion</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-gray-300 text-xs">Nombre *</Label>
            <Input data-testid="method-name-input" value={form.name} onChange={e => handleChange('name', e.target.value)} className="bg-gray-800 border-gray-600 text-white" placeholder="Ej: Superserie" />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Slug (auto)</Label>
            <Input value={form.slug} onChange={e => handleChange('slug', e.target.value)} className="bg-gray-800 border-gray-600 text-white" />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Estado</Label>
            <Select value={form.status} onValueChange={v => handleChange('status', v)}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Activo</SelectItem><SelectItem value="inactive">Inactivo</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Tipo de metodo</Label>
            <Select value={form.method_type || "Otro"} onValueChange={v => handleChange('method_type', v)}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>{METHOD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Icono</Label>
            <Input value={form.icon} onChange={e => handleChange('icon', e.target.value)} className="bg-gray-800 border-gray-600 text-white w-24" placeholder="Ej: ..." />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Prioridad</Label>
            <Input type="number" value={form.priority} onChange={e => handleChange('priority', parseInt(e.target.value) || 0)} className="bg-gray-800 border-gray-600 text-white w-24" />
          </div>
        </div>
      </div>

      {/* Description */}
      <div>
        <h3 className="text-sm font-semibold text-blue-400 mb-3 uppercase tracking-wide">Descripcion</h3>
        <div className="space-y-3">
          <div>
            <Label className="text-gray-300 text-xs">Descripcion corta</Label>
            <Textarea value={form.description} onChange={e => handleChange('description', e.target.value)} className="bg-gray-800 border-gray-600 text-white" rows={2} />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Instrucciones de uso</Label>
            <Textarea value={form.instructions} onChange={e => handleChange('instructions', e.target.value)} className="bg-gray-800 border-gray-600 text-white" rows={2} />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Estructura base</Label>
            <Textarea value={form.base_structure} onChange={e => handleChange('base_structure', e.target.value)} className="bg-gray-800 border-gray-600 text-white" rows={2} />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Ejemplo</Label>
            <Textarea value={form.example} onChange={e => handleChange('example', e.target.value)} className="bg-gray-800 border-gray-600 text-white" rows={2} />
          </div>
        </div>
      </div>

      {/* Config */}
      <div>
        <h3 className="text-sm font-semibold text-blue-400 mb-3 uppercase tracking-wide">Configuracion</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            ['allows_rounds', 'Rondas'], ['allows_grouped_exercises', 'Agrupar ejercicios'],
            ['allows_rest_between_exercises', 'Descanso entre ejercicios'], ['allows_rest_between_rounds', 'Descanso entre rondas'],
            ['allows_reps', 'Repeticiones'], ['allows_time', 'Tiempo'],
            ['allows_load', 'Carga'], ['allows_notes', 'Observaciones']
          ].map(([key, label]) => (
            <div key={key} className="flex items-center space-x-2">
              <Checkbox id={`method-${key}`} checked={form[key]} onCheckedChange={v => handleChange(key, v)} />
              <Label htmlFor={`method-${key}`} className="text-gray-300 text-xs cursor-pointer">{label}</Label>
            </div>
          ))}
        </div>
      </div>

      {/* Generator Behavior */}
      <div>
        <h3 className="text-sm font-semibold text-blue-400 mb-3 uppercase tracking-wide">Comportamiento en generador</h3>
        <div className="space-y-3">
          <div>
            <Label className="text-gray-300 text-xs">Compatibilidad</Label>
            <Select value={form.compatibility} onValueChange={v => handleChange('compatibility', v)}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white w-64"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Solo ejercicios individuales</SelectItem>
                <SelectItem value="block">Solo bloques</SelectItem>
                <SelectItem value="both">Ambos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Formato visual del bloque</Label>
            <Textarea value={form.visual_block_format} onChange={e => handleChange('visual_block_format', e.target.value)} className="bg-gray-800 border-gray-600 text-white" rows={2} />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-gray-700 sticky bottom-0 bg-gray-900 pb-2">
        <Button data-testid="method-save-btn" onClick={handleSubmit} disabled={saving} className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
        <Button data-testid="method-cancel-btn" onClick={onCancel} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800">
          Cancelar
        </Button>
      </div>
    </div>
  );
};

// ============= MAIN COMPONENT =============
export default function TrainingManager() {
  const [activeTab, setActiveTab] = useState('exercises');
  const [stats, setStats] = useState(null);
  const [migrating, setMigrating] = useState(false);

  // Exercises state
  const [exercises, setExercises] = useState([]);
  const [exercisesTotal, setExercisesTotal] = useState(0);
  const [exercisesPage, setExercisesPage] = useState(1);
  const [exercisesPages, setExercisesPages] = useState(1);
  const [exSearch, setExSearch] = useState('');
  const [exMuscle, setExMuscle] = useState('');
  const [exEquipment, setExEquipment] = useState('');
  const [exLevel, setExLevel] = useState('');
  const [exStatus, setExStatus] = useState('');
  const [exLocation, setExLocation] = useState('');
  const [exSex, setExSex] = useState('');
  const [exHasVideo, setExHasVideo] = useState(''); // 2026-02 · 'no' = sin video asignado
  const [backfilling, setBackfilling] = useState(false);
  const [upgradingSeries, setUpgradingSeries] = useState(false);
  const [coverageReport, setCoverageReport] = useState(null);
  const [loadingExercises, setLoadingExercises] = useState(false);
  const [exerciseDialogOpen, setExerciseDialogOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [savingExercise, setSavingExercise] = useState(false);

  // Methods state
  const [methods, setMethods] = useState([]);
  const [methodsTotal, setMethodsTotal] = useState(0);
  const [methodsPage, setMethodsPage] = useState(1);
  const [methodsPages, setMethodsPages] = useState(1);
  const [mtSearch, setMtSearch] = useState('');
  const [mtType, setMtType] = useState('');
  const [mtStatus, setMtStatus] = useState('');
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [methodDialogOpen, setMethodDialogOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [savingMethod, setSavingMethod] = useState(false);

  // ============= DATA FETCHING =============
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/training-manager/stats`);
      const data = await res.json();
      setStats(data);
    } catch (e) { console.error('Error fetching stats:', e); }
  }, []);

  const fetchExercises = useCallback(async () => {
    setLoadingExercises(true);
    try {
      const params = new URLSearchParams({ page: exercisesPage, limit: 25 });
      if (exSearch) params.append('search', exSearch);
      if (exMuscle) params.append('muscle', exMuscle);
      if (exEquipment) params.append('equipment', exEquipment);
      if (exLevel) params.append('level', exLevel);
      if (exStatus) params.append('status', exStatus);
      if (exLocation) params.append('location', exLocation);
      if (exSex) params.append('sex', exSex);
      if (exHasVideo) params.append('has_video', exHasVideo);
      const res = await fetch(`${API_URL}/api/admin/training-manager/exercises?${params}`);
      const data = await res.json();
      setExercises(data.exercises || []);
      setExercisesTotal(data.total || 0);
      setExercisesPages(data.pages || 1);
    } catch (e) {
      console.error('Error fetching exercises:', e);
      toast.error('Error al cargar ejercicios');
    } finally { setLoadingExercises(false); }
  }, [exercisesPage, exSearch, exMuscle, exEquipment, exLevel, exStatus, exLocation, exSex, exHasVideo]);

  const fetchMethods = useCallback(async () => {
    setLoadingMethods(true);
    try {
      const params = new URLSearchParams({ page: methodsPage, limit: 25 });
      if (mtSearch) params.append('search', mtSearch);
      if (mtType) params.append('method_type', mtType);
      if (mtStatus) params.append('status', mtStatus);
      const res = await fetch(`${API_URL}/api/admin/training-manager/methods?${params}`);
      const data = await res.json();
      setMethods(data.methods || []);
      setMethodsTotal(data.total || 0);
      setMethodsPages(data.pages || 1);
    } catch (e) {
      console.error('Error fetching methods:', e);
      toast.error('Error al cargar metodos');
    } finally { setLoadingMethods(false); }
  }, [methodsPage, mtSearch, mtType, mtStatus]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchExercises(); }, [fetchExercises]);
  useEffect(() => { fetchMethods(); }, [fetchMethods]);

  // ============= MIGRATION =============
  const handleMigrate = async () => {
    setMigrating(true);
    try {
      const [exRes, mtRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/training-manager/migrate-exercises`, { method: 'POST' }),
        fetch(`${API_URL}/api/admin/training-manager/migrate-methods`, { method: 'POST' })
      ]);
      const exData = await exRes.json();
      const mtData = await mtRes.json();
      toast.success(`${exData.message} | ${mtData.message}`);
      fetchStats();
      fetchExercises();
      fetchMethods();
    } catch (e) {
      toast.error('Error en la migracion');
    } finally { setMigrating(false); }
  };

  // ============= EXERCISE CRUD =============
  const handleSaveExercise = async (data) => {
    setSavingExercise(true);
    try {
      const isEditing = !!editingExercise?.id;
      const url = isEditing
        ? `${API_URL}/api/admin/training-manager/exercises/${editingExercise.id}`
        : `${API_URL}/api/admin/training-manager/exercises`;
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al guardar');
      }
      toast.success(isEditing ? 'Ejercicio actualizado' : 'Ejercicio creado');
      setExerciseDialogOpen(false);
      setEditingExercise(null);
      fetchExercises();
      fetchStats();
    } catch (e) { toast.error(e.message); }
    finally { setSavingExercise(false); }
  };

  const handleDuplicateExercise = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/training-manager/exercises/${id}/duplicate`, { method: 'POST' });
      if (!res.ok) throw new Error('Error al duplicar');
      toast.success('Ejercicio duplicado');
      fetchExercises();
      fetchStats();
    } catch (e) { toast.error(e.message); }
  };

  const handleToggleExercise = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/training-manager/exercises/${id}/toggle-status`, { method: 'PATCH' });
      if (!res.ok) throw new Error('Error al cambiar estado');
      const data = await res.json();
      toast.success(`Ejercicio ${data.status === 'active' ? 'activado' : 'desactivado'}`);
      fetchExercises();
      fetchStats();
    } catch (e) { toast.error(e.message); }
  };

  // ============= SEX BACKFILL + INLINE EDIT =============
  const handleBackfillSex = async () => {
    if (!window.confirm('¿Ejecutar la migración de sexo en el catálogo de ejercicios?\n\nSe rellenará `recommended_sex="ambos"` en todos los ejercicios que no tengan el campo. Es seguro correr varias veces.')) return;
    setBackfilling(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/training-manager/exercises/backfill-sex`, { method: 'POST' });
      if (!res.ok) throw new Error('Error en la migración');
      const data = await res.json();
      toast.success(data.message || `Migración completa: ${data.updated} ejercicios actualizados.`);
      fetchExercises();
    } catch (e) { toast.error(e.message); }
    finally { setBackfilling(false); }
  };

  const handleUpdateExerciseSex = async (id, newSex) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/training-manager/exercises/${id}/sex`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recommended_sex: newSex })
      });
      if (!res.ok) throw new Error('Error al actualizar sexo');
      // Actualización optimista local (sin refrescar todo el listado)
      setExercises((prev) => prev.map((ex) => ex.id === id ? { ...ex, recommended_sex: newSex } : ex));
    } catch (e) { toast.error(e.message); }
  };

  const handleUpgradeSeries = async () => {
    if (!window.confirm('¿Convertir series_detail legacy a formato estructurado?\n\nEsto NO regenera las rutinas — solo convierte "3x8-10" en objetos con técnica por serie. Es seguro y reversible.')) return;
    setUpgradingSeries(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/training-manager/workouts/upgrade-series-detail`, { method: 'POST' });
      if (!res.ok) throw new Error('Error en la migración');
      const data = await res.json();
      toast.success(data.message || `Migración completa: ${data.exercises_upgraded} ejercicios actualizados.`);
    } catch (e) { toast.error(e.message); }
    finally { setUpgradingSeries(false); }
  };

  const handleCheckCoverage = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/training-manager/videos/library-coverage`);
      if (!res.ok) throw new Error('Error obteniendo cobertura');
      const data = await res.json();
      setCoverageReport(data);
    } catch (e) { toast.error(e.message); }
  };

  // ============= METHOD CRUD =============
  const handleSaveMethod = async (data) => {
    setSavingMethod(true);
    try {
      const isEditing = !!editingMethod?.id;
      const url = isEditing
        ? `${API_URL}/api/admin/training-manager/methods/${editingMethod.id}`
        : `${API_URL}/api/admin/training-manager/methods`;
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al guardar');
      }
      toast.success(isEditing ? 'Metodo actualizado' : 'Metodo creado');
      setMethodDialogOpen(false);
      setEditingMethod(null);
      fetchMethods();
      fetchStats();
    } catch (e) { toast.error(e.message); }
    finally { setSavingMethod(false); }
  };

  const handleDuplicateMethod = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/training-manager/methods/${id}/duplicate`, { method: 'POST' });
      if (!res.ok) throw new Error('Error al duplicar');
      toast.success('Metodo duplicado');
      fetchMethods();
      fetchStats();
    } catch (e) { toast.error(e.message); }
  };

  const handleToggleMethod = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/training-manager/methods/${id}/toggle-status`, { method: 'PATCH' });
      if (!res.ok) throw new Error('Error al cambiar estado');
      const data = await res.json();
      toast.success(`Metodo ${data.status === 'active' ? 'activado' : 'desactivado'}`);
      fetchMethods();
      fetchStats();
    } catch (e) { toast.error(e.message); }
  };

  // ============= RENDER =============
  return (
    <div className="space-y-6" data-testid="training-manager">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FaDumbbell className="text-orange-400" /> Gestor de Entrenamiento
          </h1>
          <p className="text-gray-400 text-sm mt-1">Base maestra de ejercicios y metodos de entrenamiento</p>
        </div>
        {stats && stats.total_exercises === 0 && (
          <Button
            data-testid="migrate-btn"
            onClick={handleMigrate}
            disabled={migrating}
            className="bg-gradient-to-r from-amber-500 to-orange-600 text-white"
          >
            {migrating ? 'Migrando...' : 'Migrar datos existentes'}
          </Button>
        )}
        {stats && stats.total_exercises > 0 && (
          <>
            <Button
              data-testid="backfill-sex-btn"
              onClick={handleBackfillSex}
              disabled={backfilling}
              className="bg-gradient-to-r from-pink-500 to-fuchsia-600 text-white"
              title="Rellena `recommended_sex=ambos` en todos los ejercicios que no lo tengan"
            >
              {backfilling ? '⏳ Migrando sexo...' : '👥 Migración sexo (backfill)'}
            </Button>
            <Button
              data-testid="upgrade-series-btn"
              onClick={handleUpgradeSeries}
              disabled={upgradingSeries}
              className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white"
              title="Convierte series_detail legacy (strings) a formato estructurado con técnica por serie"
            >
              {upgradingSeries ? '⏳ Migrando series...' : '⚙️ Migrar series (per-serie)'}
            </Button>
            <Button
              data-testid="coverage-btn"
              onClick={handleCheckCoverage}
              className="bg-gradient-to-r from-amber-500 to-orange-600 text-white"
              title="Muestra qué grupos musculares no tienen suficientes videos por sexo — evita días de rutina con músculos faltantes"
            >
              📊 Cobertura de biblioteca
            </Button>
          </>
        )}
      </div>

      {/* Stats */}
      <TrainingStats stats={stats} />

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        <Button
          data-testid="tab-exercises"
          onClick={() => setActiveTab('exercises')}
          className={activeTab === 'exercises'
            ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
            : 'bg-transparent text-gray-400 hover:text-white hover:bg-gray-800'}
        >
          <FaDumbbell className="mr-2" /> Ejercicios ({exercisesTotal})
        </Button>
        <Button
          data-testid="tab-methods"
          onClick={() => setActiveTab('methods')}
          className={activeTab === 'methods'
            ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'
            : 'bg-transparent text-gray-400 hover:text-white hover:bg-gray-800'}
        >
          <FaCog className="mr-2" /> Metodos ({methodsTotal})
        </Button>
      </div>

      {/* ============= EXERCISES TAB ============= */}
      {activeTab === 'exercises' && (
        <div className="space-y-4">
          {/* Filters */}
          <Card className="bg-gray-900/50 border-gray-700">
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <Input
                      data-testid="exercise-search"
                      value={exSearch}
                      onChange={e => { setExSearch(e.target.value); setExercisesPage(1); }}
                      placeholder="Buscar ejercicio..."
                      className="pl-10 bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <Select value={exMuscle || "all"} onValueChange={v => { setExMuscle(v === "all" ? "" : v); setExercisesPage(1); }}>
                  <SelectTrigger className="w-[150px] bg-gray-800 border-gray-600 text-white text-xs"><SelectValue placeholder="Musculo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los musculos</SelectItem>
                    {MUSCLE_GROUPS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={exEquipment || "all"} onValueChange={v => { setExEquipment(v === "all" ? "" : v); setExercisesPage(1); }}>
                  <SelectTrigger className="w-[140px] bg-gray-800 border-gray-600 text-white text-xs"><SelectValue placeholder="Implemento" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todo implemento</SelectItem>
                    {EQUIPMENT_TYPES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={exLevel || "all"} onValueChange={v => { setExLevel(v === "all" ? "" : v); setExercisesPage(1); }}>
                  <SelectTrigger className="w-[130px] bg-gray-800 border-gray-600 text-white text-xs"><SelectValue placeholder="Nivel" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todo nivel</SelectItem>
                    {LEVELS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={exStatus || "all"} onValueChange={v => { setExStatus(v === "all" ? "" : v); setExercisesPage(1); }}>
                  <SelectTrigger className="w-[120px] bg-gray-800 border-gray-600 text-white text-xs"><SelectValue placeholder="Estado" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="active">Activos</SelectItem>
                    <SelectItem value="inactive">Inactivos</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={exLocation || "all"} onValueChange={v => { setExLocation(v === "all" ? "" : v); setExercisesPage(1); }}>
                  <SelectTrigger className="w-[130px] bg-gray-800 border-gray-600 text-white text-xs" data-testid="ex-location-filter"><SelectValue placeholder="Lugar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Cualquier lugar</SelectItem>
                    <SelectItem value="casa">🏠 Casa</SelectItem>
                    <SelectItem value="gym">🏋️ Gym</SelectItem>
                    <SelectItem value="ambos">🔀 Ambos</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={exSex || "all"} onValueChange={v => { setExSex(v === "all" ? "" : v); setExercisesPage(1); }}>
                  <SelectTrigger className="w-[130px] bg-gray-800 border-gray-600 text-white text-xs" data-testid="ex-sex-filter"><SelectValue placeholder="Sexo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Cualquier sexo</SelectItem>
                    <SelectItem value="hombre">👨 Hombre</SelectItem>
                    <SelectItem value="mujer">👩 Mujer</SelectItem>
                    <SelectItem value="ambos">⚖️ Ambos</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={exHasVideo || "all"} onValueChange={v => { setExHasVideo(v === "all" ? "" : v); setExercisesPage(1); }}>
                  <SelectTrigger className="w-[150px] bg-gray-800 border-gray-600 text-white text-xs" data-testid="ex-has-video-filter"><SelectValue placeholder="Video" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos (con/sin video)</SelectItem>
                    <SelectItem value="no">🎥 Sin video asignado</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  data-testid="new-exercise-btn"
                  onClick={() => { setEditingExercise(null); setExerciseDialogOpen(true); }}
                  className="bg-gradient-to-r from-orange-500 to-red-600 text-white"
                >
                  <FaPlus className="mr-1" /> Nuevo
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          {loadingExercises ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-orange-500 border-t-transparent"></div></div>
          ) : exercises.length === 0 ? (
            <Card className="bg-gray-900/50 border-gray-700">
              <CardContent className="py-12 text-center">
                <FaDumbbell className="text-gray-600 text-4xl mx-auto mb-3" />
                <p className="text-gray-400">No hay ejercicios. {stats?.total_exercises === 0 ? 'Haz clic en "Migrar datos existentes" para importar los ejercicios actuales.' : 'Ajusta los filtros de busqueda.'}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="exercises-table">
                <thead>
                  <tr className="border-b border-gray-700 text-gray-400 text-xs uppercase">
                    <th className="text-left py-3 px-3">Nombre</th>
                    <th className="text-left py-3 px-2">Musculo</th>
                    <th className="text-left py-3 px-2">Categoria</th>
                    <th className="text-left py-3 px-2">Implemento</th>
                    <th className="text-left py-3 px-2">Lugar</th>
                    <th className="text-left py-3 px-2">Sexo</th>
                    <th className="text-left py-3 px-2">Nivel</th>
                    <th className="text-center py-3 px-2">Estado</th>
                    <th className="text-right py-3 px-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {[...exercises].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es')).map(ex => (
                    <tr key={ex.id} className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors" data-testid={`exercise-row-${ex.id}`}>
                      <td className="py-2.5 px-3">
                        <span className="text-white font-medium">{ex.name}</span>
                        {ex.compound_type === 'compuesto' && <Badge className="ml-2 bg-purple-600/20 text-purple-300 text-[10px]">Compuesto</Badge>}
                        {ex.laterality === 'unilateral' && <Badge className="ml-1 bg-cyan-600/20 text-cyan-300 text-[10px]">Uni</Badge>}
                      </td>
                      <td className="py-2.5 px-2 text-gray-300 capitalize">{ex.primary_muscle}</td>
                      <td className="py-2.5 px-2 text-gray-300 capitalize">{ex.category}</td>
                      <td className="py-2.5 px-2 text-gray-300 capitalize">{ex.equipment?.replace('_', ' ')}</td>
                      <td className="py-2.5 px-2">
                        <Badge className={`text-[10px] ${ex.location === 'gym' ? 'bg-cyan-600/20 text-cyan-300' : ex.location === 'casa' ? 'bg-emerald-600/20 text-emerald-300' : 'bg-gray-600/20 text-gray-300'}`}>
                          {ex.location === 'gym' ? '🏋️ Gym' : ex.location === 'casa' ? '🏠 Casa' : '🔀 Ambos'}
                        </Badge>
                      </td>
                      <td className="py-2.5 px-2">
                        <div className="flex gap-1" data-testid={`sex-toggle-${ex.id}`}>
                          {[
                            { value: 'hombre', icon: '👨', title: 'Hombre', bg: 'bg-blue-600/40', activeBg: 'bg-blue-500 ring-2 ring-blue-300', text: 'text-blue-100' },
                            { value: 'mujer', icon: '👩', title: 'Mujer', bg: 'bg-pink-600/40', activeBg: 'bg-pink-500 ring-2 ring-pink-300', text: 'text-pink-100' },
                            { value: 'ambos', icon: '⚖️', title: 'Ambos', bg: 'bg-gray-600/40', activeBg: 'bg-gray-400 ring-2 ring-gray-200', text: 'text-gray-100' },
                          ].map((opt) => {
                            const active = (ex.recommended_sex || 'ambos') === opt.value;
                            return (
                              <button
                                key={opt.value}
                                data-testid={`sex-btn-${ex.id}-${opt.value}`}
                                onClick={(e) => { e.stopPropagation(); if (!active) handleUpdateExerciseSex(ex.id, opt.value); }}
                                title={opt.title}
                                className={`w-7 h-7 rounded-md flex items-center justify-center text-sm transition-all hover:brightness-125 ${active ? opt.activeBg : opt.bg} ${opt.text}`}
                              >
                                {opt.icon}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td className="py-2.5 px-2">
                        <Badge className={`text-[10px] ${ex.level === 'avanzado' ? 'bg-red-600/20 text-red-300' : ex.level === 'intermedio' ? 'bg-yellow-600/20 text-yellow-300' : 'bg-green-600/20 text-green-300'}`}>
                          {ex.level}
                        </Badge>
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        <Badge className={ex.status === 'active' ? 'bg-emerald-600/20 text-emerald-300' : 'bg-gray-600/20 text-gray-400'}>
                          {ex.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm" variant="ghost"
                            data-testid={`edit-exercise-${ex.id}`}
                            onClick={() => { setEditingExercise(ex); setExerciseDialogOpen(true); }}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 h-7 w-7 p-0"
                          ><FaEdit size={12} /></Button>
                          <Button
                            size="sm" variant="ghost"
                            data-testid={`duplicate-exercise-${ex.id}`}
                            onClick={() => handleDuplicateExercise(ex.id)}
                            className="text-gray-400 hover:text-white hover:bg-gray-700 h-7 w-7 p-0"
                          ><FaCopy size={12} /></Button>
                          <Button
                            size="sm" variant="ghost"
                            data-testid={`toggle-exercise-${ex.id}`}
                            onClick={() => handleToggleExercise(ex.id)}
                            className={`h-7 w-7 p-0 ${ex.status === 'active' ? 'text-emerald-400 hover:text-red-400' : 'text-gray-500 hover:text-emerald-400'}`}
                          >{ex.status === 'active' ? <FaToggleOn size={14} /> : <FaToggleOff size={14} />}</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Pagination */}
              {exercisesPages > 1 && (
                <div className="flex items-center justify-between mt-4 px-2">
                  <span className="text-gray-400 text-xs">{exercisesTotal} ejercicios | Pagina {exercisesPage} de {exercisesPages}</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="border-gray-600 text-gray-300 h-7" disabled={exercisesPage <= 1} onClick={() => setExercisesPage(p => p - 1)}>
                      <FaChevronLeft size={10} />
                    </Button>
                    <Button size="sm" variant="outline" className="border-gray-600 text-gray-300 h-7" disabled={exercisesPage >= exercisesPages} onClick={() => setExercisesPage(p => p + 1)}>
                      <FaChevronRight size={10} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ============= METHODS TAB ============= */}
      {activeTab === 'methods' && (
        <div className="space-y-4">
          {/* Filters */}
          <Card className="bg-gray-900/50 border-gray-700">
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <Input
                      data-testid="method-search"
                      value={mtSearch}
                      onChange={e => { setMtSearch(e.target.value); setMethodsPage(1); }}
                      placeholder="Buscar metodo..."
                      className="pl-10 bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <Select value={mtType || "all"} onValueChange={v => { setMtType(v === "all" ? "" : v); setMethodsPage(1); }}>
                  <SelectTrigger className="w-[140px] bg-gray-800 border-gray-600 text-white text-xs"><SelectValue placeholder="Tipo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    {METHOD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={mtStatus || "all"} onValueChange={v => { setMtStatus(v === "all" ? "" : v); setMethodsPage(1); }}>
                  <SelectTrigger className="w-[120px] bg-gray-800 border-gray-600 text-white text-xs"><SelectValue placeholder="Estado" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="active">Activos</SelectItem>
                    <SelectItem value="inactive">Inactivos</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  data-testid="new-method-btn"
                  onClick={() => { setEditingMethod(null); setMethodDialogOpen(true); }}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white"
                >
                  <FaPlus className="mr-1" /> Nuevo
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          {loadingMethods ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div></div>
          ) : methods.length === 0 ? (
            <Card className="bg-gray-900/50 border-gray-700">
              <CardContent className="py-12 text-center">
                <FaCog className="text-gray-600 text-4xl mx-auto mb-3" />
                <p className="text-gray-400">No hay metodos. {stats?.total_methods === 0 ? 'Haz clic en "Migrar datos existentes" para importar los metodos actuales.' : 'Ajusta los filtros.'}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="methods-table">
                <thead>
                  <tr className="border-b border-gray-700 text-gray-400 text-xs uppercase">
                    <th className="text-left py-3 px-3">Nombre</th>
                    <th className="text-left py-3 px-2">Tipo</th>
                    <th className="text-left py-3 px-2">Descripcion</th>
                    <th className="text-center py-3 px-2">Estado</th>
                    <th className="text-right py-3 px-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {methods.map(m => (
                    <tr key={m.id} className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors" data-testid={`method-row-${m.id}`}>
                      <td className="py-2.5 px-3">
                        <span className="text-white font-medium">{m.icon} {m.name}</span>
                      </td>
                      <td className="py-2.5 px-2">
                        <Badge className={`text-[10px] ${m.method_type === 'Volumen' ? 'bg-red-600/20 text-red-300' : m.method_type === 'Densidad' ? 'bg-blue-600/20 text-blue-300' : m.method_type === 'Control' ? 'bg-gray-500/20 text-gray-300' : 'bg-gray-600/20 text-gray-400'}`}>
                          {m.method_type || 'Sin tipo'}
                        </Badge>
                      </td>
                      <td className="py-2.5 px-2 text-gray-400 text-xs max-w-[300px] truncate">{m.description}</td>
                      <td className="py-2.5 px-2 text-center">
                        <Badge className={m.status === 'active' ? 'bg-emerald-600/20 text-emerald-300' : 'bg-gray-600/20 text-gray-400'}>
                          {m.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm" variant="ghost"
                            data-testid={`edit-method-${m.id}`}
                            onClick={() => { setEditingMethod(m); setMethodDialogOpen(true); }}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 h-7 w-7 p-0"
                          ><FaEdit size={12} /></Button>
                          <Button
                            size="sm" variant="ghost"
                            data-testid={`duplicate-method-${m.id}`}
                            onClick={() => handleDuplicateMethod(m.id)}
                            className="text-gray-400 hover:text-white hover:bg-gray-700 h-7 w-7 p-0"
                          ><FaCopy size={12} /></Button>
                          <Button
                            size="sm" variant="ghost"
                            data-testid={`toggle-method-${m.id}`}
                            onClick={() => handleToggleMethod(m.id)}
                            className={`h-7 w-7 p-0 ${m.status === 'active' ? 'text-emerald-400 hover:text-red-400' : 'text-gray-500 hover:text-emerald-400'}`}
                          >{m.status === 'active' ? <FaToggleOn size={14} /> : <FaToggleOff size={14} />}</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Pagination */}
              {methodsPages > 1 && (
                <div className="flex items-center justify-between mt-4 px-2">
                  <span className="text-gray-400 text-xs">{methodsTotal} metodos | Pagina {methodsPage} de {methodsPages}</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="border-gray-600 text-gray-300 h-7" disabled={methodsPage <= 1} onClick={() => setMethodsPage(p => p - 1)}>
                      <FaChevronLeft size={10} />
                    </Button>
                    <Button size="sm" variant="outline" className="border-gray-600 text-gray-300 h-7" disabled={methodsPage >= methodsPages} onClick={() => setMethodsPage(p => p + 1)}>
                      <FaChevronRight size={10} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ============= EXERCISE DIALOG ============= */}
      <Dialog open={exerciseDialogOpen} onOpenChange={setExerciseDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-3xl max-h-[90vh]" aria-describedby="exercise-dialog-desc">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FaDumbbell className="text-orange-400" />
              {editingExercise ? 'Editar Ejercicio' : 'Nuevo Ejercicio'}
            </DialogTitle>
            <p id="exercise-dialog-desc" className="sr-only">Formulario para crear o editar un ejercicio</p>
          </DialogHeader>
          <ExerciseForm
            exercise={editingExercise}
            onSave={handleSaveExercise}
            onCancel={() => { setExerciseDialogOpen(false); setEditingExercise(null); }}
            saving={savingExercise}
          />
        </DialogContent>
      </Dialog>

      {/* ============= METHOD DIALOG ============= */}
      <Dialog open={methodDialogOpen} onOpenChange={setMethodDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-3xl max-h-[90vh]" aria-describedby="method-dialog-desc">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FaCog className="text-blue-400" />
              {editingMethod ? 'Editar Metodo' : 'Nuevo Metodo'}
            </DialogTitle>
            <p id="method-dialog-desc" className="sr-only">Formulario para crear o editar un metodo de entrenamiento</p>
          </DialogHeader>
          <MethodForm
            method={editingMethod}
            onSave={handleSaveMethod}
            onCancel={() => { setMethodDialogOpen(false); setEditingMethod(null); }}
            saving={savingMethod}
          />
        </DialogContent>
      </Dialog>

      {/* ═══ Modal: Reporte de cobertura de biblioteca de VIDEOS ═══ */}
      {coverageReport && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
          onClick={() => setCoverageReport(null)}
          data-testid="coverage-report-modal"
        >
          <div
            className="w-full max-w-3xl max-h-[85vh] rounded-2xl overflow-hidden flex flex-col bg-gray-900 border border-orange-500/40"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-orange-500/30 bg-gradient-to-r from-orange-900/40 to-amber-900/40">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-orange-300 flex items-center gap-2">
                    📊 Cobertura de biblioteca de videos
                  </h3>
                  <p className="text-xs text-orange-200/70 mt-0.5">
                    Total videos escaneados: {coverageReport.total_videos_scanned} · Mínimo recomendado por (músculo × sexo): {coverageReport.recommended_min_per_combo}
                  </p>
                </div>
                <button
                  onClick={() => setCoverageReport(null)}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 text-white"
                >✕</button>
              </div>
              {coverageReport.gaps.length > 0 && (
                <div className="mt-3 text-xs text-orange-200 bg-orange-500/10 border border-orange-500/30 rounded p-2">
                  ⚠️ <strong>{coverageReport.gaps.length}</strong> músculos con cobertura insuficiente. Los días de rutina que incluyan estos músculos pueden aparecer con menos ejercicios de lo esperado.
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <table className="w-full text-xs">
                <thead className="text-gray-400 uppercase text-[10px] border-b border-gray-700">
                  <tr>
                    <th className="text-left py-2">Músculo</th>
                    <th className="text-center py-2">👨 Hombre</th>
                    <th className="text-center py-2">👩 Mujer</th>
                    <th className="text-center py-2">⚖️ Ambos</th>
                    <th className="text-center py-2">Total hombre</th>
                    <th className="text-center py-2">Total mujer</th>
                  </tr>
                </thead>
                <tbody>
                  {coverageReport.muscles.map((r) => (
                    <tr key={r.muscle} className="border-b border-gray-800/60">
                      <td className="py-2 text-white font-semibold capitalize">{r.muscle}</td>
                      <td className="text-center text-blue-300">{r.hombre_only}</td>
                      <td className="text-center text-pink-300">{r.mujer_only}</td>
                      <td className="text-center text-gray-300">{r.ambos}</td>
                      <td className={`text-center font-bold ${r.hombre_ok ? 'text-emerald-400' : 'text-red-400'}`}>
                        {r.hombre_ok ? '✅' : '⚠️'} {r.available_for_hombre}
                      </td>
                      <td className={`text-center font-bold ${r.mujer_ok ? 'text-emerald-400' : 'text-red-400'}`}>
                        {r.mujer_ok ? '✅' : '⚠️'} {r.available_for_mujer}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {Object.keys(coverageReport.unknown_muscles || {}).length > 0 && (
                <div className="mt-4 p-3 rounded bg-yellow-900/20 border border-yellow-500/30">
                  <p className="text-yellow-300 text-xs font-semibold mb-1">
                    Músculos no reconocidos ({Object.keys(coverageReport.unknown_muscles).length}):
                  </p>
                  <p className="text-yellow-200/70 text-xs">
                    Estos tags no están en el mapeo canónico y no se usan en la generación. Considera renombrarlos:
                  </p>
                  <ul className="mt-1 text-yellow-200/70 text-xs">
                    {Object.entries(coverageReport.unknown_muscles).map(([m, n]) => (
                      <li key={m}>· <strong>{m}</strong> ({n} videos)</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
